import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@astro/api'
import AstrologerCard from '../astrologers/AstrologerCard'
import { decorateAstrologer } from '../astrologers/astrologersData'
import { useReviewSummaries } from '../../../hooks/useReviews'
import { isExcludedAstrologer } from '../../../utils/excludedAstrologers'

/** Fisher-Yates shuffle so the 4 astrologers shown vary between visits. */
function shuffle(list) {
  const arr = [...list]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

function SkeletonCard() {
  return (
    <div className="rounded-3xl overflow-hidden animate-pulse" style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.12)' }}>
      <div style={{ height: 272, background: 'rgba(200,146,42,.10)' }} />
      <div className="p-5 space-y-3">
        <div className="h-4 w-2/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
        <div className="h-2.5 w-1/2 rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
        <div className="h-8 rounded-xl" style={{ background: 'rgba(200,146,42,.10)' }} />
      </div>
    </div>
  )
}

export default function OtherAstrologersSection({ excludeId }) {
  const { data: result, isPending, isError } = useQuery({
    queryKey: ['astrologersList', 'maharaj-booking-alternatives'],
    queryFn: () => api.astrologers.list({ page: 1, per_page: 20 }),
  })

  const astrologers = useMemo(() => {
    const list = (result?.data ?? [])
      .filter((a) => String(a.id) !== String(excludeId) && !isExcludedAstrologer(a))
      .map(decorateAstrologer)
    return shuffle(list).slice(0, 4)
  }, [result, excludeId])

  const liveRatings = useReviewSummaries('astrologer', astrologers.map((a) => a.id))

  if (!isPending && !isError && astrologers.length === 0) return null

  return (
    <section className="container px-6 mx-auto mt-16 relative">
      <div className="text-center mb-10 space-y-4">
        <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full" style={{ border: '1px solid rgba(200,146,42,.25)', background: 'rgba(200,146,42,.06)' }}>
          <span className="text-xs leading-none" style={{ color: '#C8922A' }}>ॐ</span>
          <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
          <span className="text-[10px] uppercase font-semibold tracking-[.35em] font-cinzel" style={{ color: 'rgba(200,146,42,.8)' }}>Explore More Experts</span>
          <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
          <span className="text-xs leading-none" style={{ color: '#C8922A' }}>ॐ</span>
        </div>
        <h2 className="font-cinzel text-3xl sm:text-4xl font-normal tracking-[.02em] leading-tight" style={{ color: '#1C140A' }}>
          Prefer to Talk to Someone
          <span style={{ background: 'linear-gradient(90deg,#C8922A,#E5A820,#C8922A)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}> Right Now?</span>
        </h2>
        <p className="text-sm sm:text-base font-light max-w-xl mx-auto" style={{ color: '#8B7355' }}>
          Meet other verified astrologers who are live and ready to guide you — book a session instantly.
        </p>
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : isError ? (
        <p className="text-sm text-center" style={{ color: '#8B7355' }}>Couldn't load other astrologers right now.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {astrologers.map((astro) => (
            <AstrologerCard key={astro.id} astro={astro} liveRating={liveRatings[astro.id]} />
          ))}
        </div>
      )}

      <div className="text-center mt-10">
        <Link
          to="/consultations"
          className="inline-flex items-center gap-3 font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 group"
          style={{ background: '#1C140A', color: '#FFF', boxShadow: '0 8px 32px -8px rgba(28,20,10,.35)' }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#C8922A'; e.currentTarget.style.boxShadow = '0 8px 32px -8px rgba(200,146,42,.5)' }}
          onMouseOut={(e) => { e.currentTarget.style.background = '#1C140A'; e.currentTarget.style.boxShadow = '0 8px 32px -8px rgba(28,20,10,.35)' }}
        >
          Meet All Astrologers
          <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>
    </section>
  )
}
